import { apiErrorMessage, apiErrorStatus } from '@/api/client';
import { documentsApi } from '@/api/documents.api';
import { ACTIVE_JOB_STATUSES, type DetectionStatusModel } from '@/types/documents.types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Poll interval while the run is young: steps flip every second or two at this stage. */
const FAST_INTERVAL_MS = 700;

/** Poll interval once the run has settled into the long LLM step. */
const SLOW_INTERVAL_MS = 2000;

/** How long after the first poll the fast cadence lasts. */
const FAST_WINDOW_MS = 30_000;

export interface DetectionPollingState {
  /** The latest status, or `null` before the first answer and whenever polling is disabled. */
  status: DetectionStatusModel | null;
  /** True while requests are still being scheduled. */
  isPolling: boolean;
  /** A transient failure of the last poll; cleared by the next successful one. */
  error: string | null;
  /** The backend answered 404: the session is gone and the stored id is stale. */
  isExpired: boolean;
  /** One extra request outside the schedule. */
  refresh: () => Promise<void>;
}

const isTerminal = (status: DetectionStatusModel | null): boolean =>
  status !== null && !ACTIVE_JOB_STATUSES.includes(status.status);

/**
 * Follows a detection run by polling `GET /documents/{id}/detection`.
 *
 * A chain of timeouts rather than an interval, so a slow answer never overlaps the next request.
 * The first request goes out immediately; the cadence is 700 ms for the first 30 s and 2 s after
 * that. Polling stops on its own once the run is `done`, `failed` or `cancelled`, on a 404, or
 * when the caller disables it or unmounts. Other failures are reported through `error` and the
 * schedule carries on — a single dropped request must not end the progress view.
 *
 * `status` is reset the moment `enabled` turns false or the document changes, so a retry on the
 * same document starts from an empty view instead of flashing the failed steps of the run before.
 */
export const useDetectionPolling = (documentId: string | null, enabled: boolean): DetectionPollingState => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<DetectionStatusModel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isExpired, setIsExpired] = useState(false);

  // The messages are localized at the time of the failure without restarting the schedule on a
  // language change — `t` changes identity then, and it must not be an effect dependency.
  const translate = useRef(t);
  useEffect(() => {
    translate.current = t;
  }, [t]);

  // One polling cycle is identified by the document it follows; `null` when nothing runs.
  const cycleKey = enabled && documentId ? documentId : null;
  const [previousCycleKey, setPreviousCycleKey] = useState(cycleKey);
  if (cycleKey !== previousCycleKey) {
    setPreviousCycleKey(cycleKey);
    setStatus(null);
    setError(null);
    setIsExpired(false);
  }

  useEffect(() => {
    if (cycleKey === null) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();

    const tick = async () => {
      try {
        const next = await documentsApi.detectionStatus(cycleKey);
        if (cancelled) return;
        setStatus(next);
        setError(null);
        if (isTerminal(next)) return;
      } catch (err) {
        if (cancelled) return;
        if (apiErrorStatus(err) === 404) {
          setIsExpired(true);
          return;
        }
        setError(apiErrorMessage(err, translate.current('documents.detection.errors.statusFailed')));
      }
      const elapsed = Date.now() - startedAt;
      timer = setTimeout(tick, elapsed < FAST_WINDOW_MS ? FAST_INTERVAL_MS : SLOW_INTERVAL_MS);
    };

    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [cycleKey]);

  const refresh = useCallback(async () => {
    if (!documentId) return;
    try {
      setStatus(await documentsApi.detectionStatus(documentId));
      setError(null);
    } catch (err) {
      if (apiErrorStatus(err) === 404) setIsExpired(true);
      else setError(apiErrorMessage(err, translate.current('documents.detection.errors.statusFailed')));
    }
  }, [documentId]);

  return {
    status,
    isPolling: cycleKey !== null && !isExpired && !isTerminal(status),
    error,
    isExpired,
    refresh,
  };
};
